/**
 * @fileoverview 排行榜页面
 * @description 展示用户收益排行榜，对接 /api/v1/public/profitRank 接口
 * 设计风格：高清奖台背景图 + 金银铜奖牌效果 + 币安深黑配色
 * @module pages/Leaderboard
 */

import { Crown, Medal, TrendingUp, Trophy } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/layout/MainLayout';
import { Identicon } from '../components/ui/Identicon';
import { useAppStore } from '../stores/useAppStore';
import { fetchProfitRank, type BackendProfitRankEntry } from '../services/userService';
import { API_OK } from '../services/api';
import { formatCurrency, cn, truncateAddress } from '../lib/utils';

// 高清排行榜背景图（本地路径）
const LEADERBOARD_BG = '/bg/leaderboard-bg.webp';

// ============================================================
// 类型定义
// ============================================================

/** 排行榜展示条目 */
interface LeaderboardEntry {
  rank: number;
  guid: number;
  username: string;
  walletAddress: string;
  /** 累计收益（USDT） */
  totalProfit: number;
}

// ============================================================
// 工具函数
// ============================================================

/** 将后端 BackendProfitRankEntry 映射为排行榜展示条目 */
function mapEntry(row: BackendProfitRankEntry, index: number): LeaderboardEntry {
  const accountStr = row.account != null ? String(row.account) : '';
  const isWallet = accountStr.startsWith('0x') && accountStr.length >= 10;
  return {
    rank: index + 1,
    guid: row.guid,
    username: row.accountName || (isWallet ? truncateAddress(accountStr) : accountStr) || `User#${row.guid}`,
    walletAddress: isWallet ? accountStr : accountStr,
    totalProfit: Number(row.totalProfit) || 0,
  };
}

// ============================================================
// 奖牌配置
// ============================================================

// MEDAL_CONFIGS labels are now resolved via i18n in LeaderboardHero
const MEDAL_CONFIGS = {
  1: {
    border: '#F0B90B',
    glow: 'rgba(240,185,11,0.45)',
    bg: 'rgba(240,185,11,0.12)',
    avatarSize: 'h-16 w-16',
    textSize: 'text-base',
    podiumH: 'h-16',
    icon: <Crown className="h-4 w-4" style={{ color: '#F0B90B' }} />,
    labelKey: 'leaderboard.gold',
    labelColor: '#F0B90B',
  },
  2: {
    border: '#C0C0C0',
    glow: 'rgba(192,192,192,0.3)',
    bg: 'rgba(192,192,192,0.08)',
    avatarSize: 'h-12 w-12',
    textSize: 'text-sm',
    podiumH: 'h-10',
    icon: <Medal className="h-3.5 w-3.5" style={{ color: '#C0C0C0' }} />,
    labelKey: 'leaderboard.silver',
    labelColor: '#C0C0C0',
  },
  3: {
    border: '#CD7F32',
    glow: 'rgba(205,127,50,0.3)',
    bg: 'rgba(205,127,50,0.08)',
    avatarSize: 'h-12 w-12',
    textSize: 'text-sm',
    podiumH: 'h-8',
    icon: <Medal className="h-3.5 w-3.5" style={{ color: '#CD7F32' }} />,
    labelKey: 'leaderboard.bronze',
    labelColor: '#CD7F32',
  },
} as const;

// ============================================================
// 子组件：奖台 Hero 区域
// ============================================================

function LeaderboardHero({ entries }: { entries: LeaderboardEntry[] }) {
  const { t } = useTranslation();
  const top3 = entries.slice(0, 3);
  const [second, first, third] = [top3[1], top3[0], top3[2]];

  const PodiumCard = ({
    entry,
    rank,
  }: {
    entry: LeaderboardEntry | undefined;
    rank: 1 | 2 | 3;
  }) => {
    if (!entry) return <div className="flex-1" />;
    const cfg = MEDAL_CONFIGS[rank];
    return (
      <div className="flex flex-col items-center gap-2 flex-1">
        <div
          className={`${cfg.avatarSize} rounded-full flex items-center justify-center relative`}
          style={{
            border: `2px solid ${cfg.border}`,
            boxShadow: `0 0 18px ${cfg.glow}`,
            background: cfg.bg,
          }}
        >
          <Identicon guid={String(entry.guid)} size={56} />
          <div
            className="absolute -bottom-1 -right-1 rounded-full p-0.5 flex items-center justify-center"
            style={{ background: '#0B0E11', border: `1px solid ${cfg.border}` }}
          >
            {cfg.icon}
          </div>
        </div>
        <div className="text-center">
          <p className={`${cfg.textSize} font-bold text-foreground truncate max-w-[80px]`}>
            {entry.username}
          </p>
          <p className="text-xs font-mono font-bold" style={{ color: cfg.labelColor }}>
            +{formatCurrency(entry.totalProfit)}
          </p>
          <p className="text-[10px] text-muted-foreground">{t(cfg.labelKey)}</p>
        </div>
        <div
          className={`w-full ${cfg.podiumH} rounded-t-lg`}
          style={{
            background: `linear-gradient(to top, ${cfg.border}30, ${cfg.border}08)`,
            border: `1px solid ${cfg.border}20`,
          }}
        />
      </div>
    );
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden mb-6"
      style={{
        backgroundImage: `url(${LEADERBOARD_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        minHeight: '220px',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E11]/60 via-[#0B0E11]/50 to-[#0B0E11]/95" />
      <div className="relative z-10 p-6">
        <div className="text-center mb-6">
          <h1
            className="text-2xl font-bold text-white"
            style={{ fontFamily: 'Space Grotesk, sans-serif', textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
          >
            <Trophy className="inline-block h-5 w-5 mr-2 text-brand" />{t('leaderboard.title')}
          </h1>
          <p className="text-white/50 text-sm mt-1">{t('leaderboard.subtitle')}</p>
        </div>
        <div className="flex items-end justify-center gap-4 max-w-xs mx-auto">
          <PodiumCard entry={second} rank={2} />
          <PodiumCard entry={first} rank={1} />
          <PodiumCard entry={third} rank={3} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 子组件：排行榜行
// ============================================================

function LeaderboardRow({
  entry,
  isCurrentUser,
  index,
}: {
  entry: LeaderboardEntry;
  isCurrentUser: boolean;
  index: number;
}) {
  const { t } = useTranslation();
  const rankDisplay = () => {
    if (entry.rank === 1) return <Crown className="h-4 w-4" style={{ color: '#F0B90B' }} />;
    if (entry.rank === 2) return <Medal className="h-4 w-4" style={{ color: '#C0C0C0' }} />;
    if (entry.rank === 3) return <Medal className="h-4 w-4" style={{ color: '#CD7F32' }} />;
    return (
      <span className="text-sm font-mono text-muted-foreground w-5 text-center">
        {entry.rank}
      </span>
    );
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3.5 transition-all duration-200',
        isCurrentUser
          ? 'bg-brand/8 border-l-2 border-brand'
          : 'hover:bg-white/3 border-l-2 border-transparent',
        index !== 0 && 'border-t border-white/4'
      )}
    >
      <div className="w-6 flex items-center justify-center shrink-0">{rankDisplay()}</div>
      <div className="h-9 w-9 shrink-0 rounded-full overflow-hidden border border-white/10">
        <Identicon guid={String(entry.guid)} size={36} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-semibold text-foreground truncate max-w-[140px]">
            {entry.username}
              {isCurrentUser && (
              <span className="ml-1.5 text-xs text-brand font-normal">({t('leaderboard.me')})</span>
            )}
          </p>
          {entry.rank <= 3 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(240,185,11,0.15) 0%, rgba(240,185,11,0.08) 100%)',
                border: '1px solid rgba(240,185,11,0.35)',
                color: '#F0B90B',
              }}
            >
              <TrendingUp className="inline-block h-3 w-3 mr-1" />{t('leaderboard.smartMoney')}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground font-mono">
          {entry.walletAddress ? truncateAddress(entry.walletAddress) : ''}
        </p>
      </div>
      <div className="text-right shrink-0 min-w-[90px]">
        <p className="text-sm font-bold font-mono text-up">
          +{formatCurrency(entry.totalProfit)}
        </p>
        <p className="text-xs text-muted-foreground">{t('leaderboard.totalProfit')}</p>
      </div>
    </div>
  );
}

// ============================================================
// 主页面组件
// ============================================================

export default function Leaderboard() {
  const { t } = useTranslation();
  const { currentUser } = useAppStore();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 加载收益排行榜数据
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const resp = await fetchProfitRank();
        if (resp.status === API_OK && Array.isArray(resp.data)) {
          setEntries(resp.data.map(mapEntry));
        }
      } catch {
        // 静默失败
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // 判断当前用户是否在榜单中（通过 guid 或 walletAddress 匹配）
  const currentUserId = currentUser?.id;
  // walletAddress 可能是数字（TG 用户），需先转字符串再 toLowerCase
  const currentWallet = currentUser?.walletAddress != null
    ? String(currentUser.walletAddress).toLowerCase()
    : undefined;
  const currentUserEntry = entries.find(
    (e) =>
      currentUser &&
      (String(e.guid) === currentUserId ||
        (currentWallet &&
          e.walletAddress != null &&
          String(e.walletAddress).toLowerCase() === currentWallet))
  );

  return (
    <MainLayout>
      <div className="container py-6">
        {/* Hero 奖台区域 */}
        <LeaderboardHero entries={entries} />

        {/* 榜单 */}
        <div className="bg-[#1E2026] rounded-2xl border border-white/6 overflow-hidden">
          {/* 标题栏 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/6">
            <div className="flex items-center gap-1.5">
              <Trophy className="h-4 w-4 text-brand" />
              <span
                className="text-sm font-bold text-foreground"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {t('leaderboard.title')}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">Top {entries.length}</span>
          </div>

          {/* 表头 */}
          <div className="flex items-center gap-3 px-4 py-2.5 bg-white/2">
            <div className="w-6" />
            <div className="w-9" />
            <div className="flex-1">
              <span className="text-xs text-muted-foreground">{t('leaderboard.user')}</span>
            </div>
            <div className="min-w-[90px] text-right">
              <span className="text-xs text-muted-foreground">{t('leaderboard.totalProfit')}</span>
            </div>
          </div>

          {/* 榜单列表 */}
          <div>
            {isLoading ? (
              <div className="py-12 text-center text-muted-foreground text-sm">{t('common.loading')}</div>
            ) : entries.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                {t('leaderboard.empty')}
              </div>
            ) : (
              entries.map((entry, index) => (
                <LeaderboardRow
                  key={entry.guid}
                  entry={entry}
                  isCurrentUser={
                    !!(currentUser && (
                      String(entry.guid) === currentUserId ||
                      (currentWallet &&
                        entry.walletAddress != null &&
                        String(entry.walletAddress).toLowerCase() === currentWallet)
                    ))
                  }
                  index={index}
                />
              ))
            )}
          </div>

          {/* 我的排名提示 */}
          {currentUser && (
            <div className="px-4 py-3 border-t border-white/6 bg-brand/4">
              <p className="text-xs text-muted-foreground text-center">
                {currentUserEntry
                  ? t('leaderboard.myRank', { rank: currentUserEntry.rank, profit: formatCurrency(currentUserEntry.totalProfit) })
                  : t('leaderboard.notRanked')}
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
